import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiAlertTriangle, FiCheckCircle, FiCreditCard, FiShield, FiZap, FiAward, FiTrendingUp, FiLogOut, FiClock } from 'react-icons/fi';
import logoImage from '../assets/icon-with-name.png';
import PrimaryButton from '../Components/Buttons/PrimaryButton';
import OutlinedPrimaryButton from '../Components/Buttons/OutlinedPrimaryButton';
import { useLanguage } from '../Context/LanguageContext';
import './subscription-expired.css';

const SubscriptionExpired = ({ userPlan: propUserPlan = 'FREE', hasExpired: propHasExpired = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  // Get values from location state if available, otherwise use props
  const userPlan = location.state?.userPlan || propUserPlan;
  const hasExpired = location.state?.hasExpired !== undefined ? location.state.hasExpired : propHasExpired;

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('brandvox_token');
    localStorage.removeItem('user_email');
    window.location.href = '/login';
  };

  // Dynamic text content based on subscription state
  const title = hasExpired
    ? t('dashboard.subscriptionexpired.subscriptionExpiredTitle')
    : t('dashboard.subscriptionexpired.upgradeRequiredTitle');

  const subtitle = hasExpired
    ? t('dashboard.subscriptionexpired.planExpiredSubtitle', { plan: userPlan })
    : t('dashboard.subscriptionexpired.upgradeSubtitle');

  const buttonText = hasExpired
    ? t('dashboard.subscriptionexpired.renewSubscriptionButton')
    : t('dashboard.subscriptionexpired.upgradePlanButton');

  // Features based on subscription state
  const features = hasExpired
    ? [
      {
        icon: <FiZap size={20} />,
        text: t('dashboard.subscriptionexpired.featureResumeService'),
        subtext: t('dashboard.subscriptionexpired.featureResumeServiceDesc')
      },
      {
        icon: <FiClock size={20} />,
        text: t('dashboard.subscriptionexpired.featureQuickReactivation'),
        subtext: t('dashboard.subscriptionexpired.featureQuickReactivationDesc')
      },
      {
        icon: <FiShield size={20} />,
        text: t('dashboard.subscriptionexpired.featureKeepSettings'),
        subtext: t('dashboard.subscriptionexpired.featureKeepSettingsDesc')
      },
    ]
    : [
      {
        icon: <FiAward size={20} />,
        text: t('dashboard.subscriptionexpired.featurePremiumFeatures'),
        subtext: t('dashboard.subscriptionexpired.featurePremiumFeaturesDesc')
      },
      {
        icon: <FiTrendingUp size={20} />,
        text: t('dashboard.subscriptionexpired.featureHigherLimits'),
        subtext: t('dashboard.subscriptionexpired.featureHigherLimitsDesc')
      },
      {
        icon: <FiShield size={20} />,
        text: t('dashboard.subscriptionexpired.featurePrioritySupport'),
        subtext: t('dashboard.subscriptionexpired.featurePrioritySupportDesc')
      },
    ];

  // Plan badge text
  const planBadgeText = t('dashboard.subscriptionexpired.planBadge', { plan: userPlan });

  return (
    <div className="subscription-expired-page">
      <div className="subscription-expired-container">
        {/* Header */}
        <div className="subscription-expired-header">
          <img src={logoImage} alt="BrandVox" className="logo" />
          <div className="subscription-expired-header-actions">
            <button
              className="header-logout-button"
              onClick={handleLogout}
              title={t('dashboard.subscriptionexpired.logoutTitle')}
              aria-label={t('dashboard.subscriptionexpired.logoutAriaLabel')}
            >
              <FiLogOut />
            </button>
          </div>
          <h1 className="title">{title}</h1>
          <p className="subtitle">{subtitle}</p>
        </div>

        {/* Content Card */}
        <div className="subscription-expired-card">
          {/* Status Section */}
          <div className="status-section">
            <div className="status-icon">
              <FiAlertTriangle size={24} />
            </div>
            <div className="status-details">
              <h2 className="status-title">
                {hasExpired
                  ? t('dashboard.subscriptionexpired.statusExpired')
                  : t('dashboard.subscriptionexpired.statusUpgradeRequired')}
              </h2>
              {hasExpired && (
                <span className="plan-badge">{planBadgeText}</span>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="features-section">
            {features.map((feature, index) => (
              <div key={index} className="feature-row">
                <div className="feature-icon-subscription-expired">
                  {feature.icon}
                </div>
                <div className="feature-content">
                  <span className="feature-label">{feature.text}</span>
                  <span className="feature-value">{feature.subtext}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <OutlinedPrimaryButton
              size="sm"
              width="100%"
              onClick={() => navigate('/account?tab=plans')}
              aria-label={buttonText}
            >
              <FiCreditCard className="btn-icon me-2" />
              {buttonText}
            </OutlinedPrimaryButton>

            <PrimaryButton
              size="sm"
              width="100%"
              onClick={() => navigate('/')}
              aria-label={t('dashboard.subscriptionexpired.goToDashboardButton')}
            >
              <FiCheckCircle className="btn-icon me-2" />
              {t('dashboard.subscriptionexpired.updatedPlanButton')}
            </PrimaryButton>
          </div>
        </div>

        {/* Footer */}
        <div className="subscription-expired-footer">
          {t('dashboard.subscriptionexpired.footerNeedHelp')}{' '}
          <a href="mailto:info@brandvoxai.com">{t('dashboard.subscriptionexpired.footerEmail')}</a>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionExpired; 